import { inject, Injectable, Signal, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable, of, switchMap, take } from 'rxjs';
import { ProtocolCardActionTaken } from 'src/app/shared/card-regenerative-protocol/card-regenerative-protocol.component';
import { environment } from '../../../environments/environment';
import {
  RegenerativeProtocol,
  RegenerativeProtocolCreate,
  RegenerativeProtocolUpdate,
} from '../_models/regenerative/regenerativeProtocol';

@Injectable({
  providedIn: 'root',
})
export class ProtocolsRegenerativeService {
  private regenerativeService = environment.regenerativeServiceUrl;
  public currentEditProtocol: RegenerativeProtocol = null;
  // Complete a Draft, Edit a Published or Watch Details --> used for managed the stepper for the different use cases
  public actionOnCurrentProtocol: ProtocolCardActionTaken = null;

  public $protocolsList: BehaviorSubject<RegenerativeProtocol[]> = new BehaviorSubject<
    RegenerativeProtocol[]
  >(null);

  // Signal to control the disabled state of the CONTINUE button in Protocol Creation
  private _isContinueButtonDisabled = signal<boolean>(true);

  // Read-only Signal to expose the state
  readonly isContinueButtonDisabled: Signal<boolean> = this._isContinueButtonDisabled.asReadonly();

  http = inject(HttpClient);
  constructor() {}

  /**
   * @description Get all protocols.
   * @returns {Promise<{ data: RegenerativeProtocol[], totalCount: number }>} A promise containing the list of protocols and the total count.
   */
  async getProtocols(): Promise<{ data: RegenerativeProtocol[]; totalCount: number }> {
    // Fetch the data from the API
    const response: { data: RegenerativeProtocol[]; totalCount: number } = await firstValueFrom(
      this.http.get<{ data: RegenerativeProtocol[]; totalCount: number }>(
        `${this.regenerativeService}api/private/v1/protocols`
      )
    );

    // Update the BehaviorSubject with the fetched data
    this.$protocolsList.next(response.data);

    // Return the data to the caller
    return response;
  }

  /**
   * @description Get a specific protocol by ID using the cached list if available, or fallback to API.
   * @param {string} protocolId - The ID of the protocol.
   * @returns {Observable<{ data: RegenerativeProtocol[], totalCount: number }>}
   * An observable emitting the matching protocol (in an array) and a dummy totalCount.
   */
  getProtocolById$(
    protocolId: string
  ): Observable<{ data: RegenerativeProtocol[]; totalCount: number }> {
    return this.$protocolsList.pipe(
      take(1), // Take the current value of the BehaviorSubject
      switchMap((protocols) => {
        const protocol = protocols?.find((p) => p.id === protocolId);

        if (protocol) {
          // Found in cache – wrap in expected format and return as observable
          return of({ data: [protocol], totalCount: 1 });
        }

        // Not found – fallback to API
        return this.http.get<{ data: RegenerativeProtocol[]; totalCount: number }>(
          `${this.regenerativeService}api/private/v1/protocols/${protocolId}`
        );
      })
    );
  }

  /**
   * @description Save a new protocol.
   * @param {RegenerativeProtocolCreate} protocol - The protocol data to create.
   * @returns {Promise<{ data: RegenerativeProtocol, totalCount: number }>} A promise containing the created protocol and the total count.
   */
  async saveProtocol(
    protocol: RegenerativeProtocolCreate
  ): Promise<{ data: RegenerativeProtocol; totalCount: number }> {
    return firstValueFrom(
      this.http.post<{ data: RegenerativeProtocol; totalCount: number }>(
        `${this.regenerativeService}api/private/v1/protocols`,
        protocol
      )
    );
  }

  /**
   * @description Update an existing protocol.
   * @param {string} protocolId - The ID of the protocol to update.
   * @param {RegenerativeProtocolUpdate} protocol - The updated protocol data.
   * @returns {Promise<{ data: RegenerativeProtocol, totalCount: number }>} A promise containing the updated protocol and the total count.
   */
  async updateProtocol(
    protocolId: string,
    protocol: RegenerativeProtocolUpdate
  ): Promise<{ data: RegenerativeProtocol; totalCount: number }> {
    return firstValueFrom(
      this.http.put<{ data: RegenerativeProtocol; totalCount: number }>(
        `${this.regenerativeService}api/private/v1/protocols/${protocolId}`,
        protocol
      )
    );
  }

  /**
   * @description Save multiple protocols in bulk.
   * @param {RegenerativeProtocolCreate[]} protocol - The array of protocols to create.
   * @returns {Promise<RegenerativeProtocol[]>} A promise containing the list of created protocols.
   */
  async saveProtocolBulk(protocol: RegenerativeProtocolCreate[]): Promise<RegenerativeProtocol[]> {
    return firstValueFrom(
      this.http.post<RegenerativeProtocol[]>(
        `${this.regenerativeService}api/private/v1/protocols`,
        protocol
      )
    );
  }

  /**
   * @description Update multiple existing protocols in bulk.
   * @param {RegenerativeProtocolUpdate[]} protocol - The array of protocols to update.
   * @returns {Promise<RegenerativeProtocol[]>} A promise containing the list of updated protocols.
   */
  async updateProtocolBulk(
    protocol: RegenerativeProtocolUpdate[]
  ): Promise<RegenerativeProtocol[]> {
    return firstValueFrom(
      this.http.put<RegenerativeProtocol[]>(
        `${this.regenerativeService}api/private/v1/protocols`,
        protocol
      )
    );
  }

  /**
   * @description Delete a specific protocol by ID.
   * @param {string} protocolId - The ID of the protocol to delete.
   * @returns {Promise<{}>} A promise containing an empty object upon successful deletion.
   */
  async deleteProtocol(protocolId: string): Promise<{}> {
    return firstValueFrom(
      this.http.delete<{}>(`${this.regenerativeService}api/private/v1/protocols/${protocolId}`)
    );
  }

  /**
   * @description Upload the cover image of a protocol into a public bucket.
   * @param {string} protocolId - The ID of the protocol.
   * @param {any} imageDataObj - An object containing the image in bytes and its name.
   * @returns {Promise<{ data: { imgUrl: string }[]; totalCount: number }>} A promise containing the image URLs and total count.
   */
  async uploadProtocolCoverImage(
    protocolId: string,
    imageDataObj: any
  ): Promise<{ data: { imgUrl: string }[]; totalCount: number }> {
    const formData: FormData = new FormData();
    formData.append('file', imageDataObj);
    return firstValueFrom(
      this.http.post<{ data: { imgUrl: string }[]; totalCount: number }>(
        `${this.regenerativeService}api/private/v1/protocols/uploadImage/${protocolId}`,
        formData
      )
    );
  }

  // Method to update the state from any stepper component
  /**
   * @description Method to update the state for CONTINUE btn disability, from any of the stepper component
   * @param {boolean} state - The state to set for the CONTINUE button.
   * @returns {void} It updates the state for the CONTINUE button disability.
   */
  setContinueButtonDisabled(state: boolean): void {
    this._isContinueButtonDisabled.set(state);
  }
}
